import React from "react";
import { Box, TextField, Typography, InputAdornment } from "@mui/material";
import LockIcon from "@mui/icons-material/LockOutlined";

// Static security code that must be typed before any destructive operation
// (delete user, delete device, remove device assignment, reassign IMEI).
// Centralised here so it can be rotated in one place if needed.
export const DANGER_CONFIRM_CODE = "65432";

export const isDangerCodeValid = (value) =>
  String(value || "").trim() === DANGER_CONFIRM_CODE;

/**
 * Inline "type security code to confirm" field used by destructive dialogs.
 *
 * Props:
 * - value: current input value (controlled)
 * - onChange: setter
 * - disabled: optional disable flag (e.g. while saving)
 */
export default function DangerConfirmField({
  value,
  onChange,
  disabled,
  label = "Security code",
}) {
  const ok = isDangerCodeValid(value);
  const showError = value && !ok;

  return (
    <Box sx={{ mt: 1.5 }}>
      <TextField
        fullWidth
        size="small"
        // type="text" (not "password") so Chrome / Edge don't latch on with
        // their login-form heuristic and auto-fill nearby text inputs (e.g.
        // the device search box on the devices page) with stored credentials.
        type="text"
        label={label}
        // Random unique name so password managers don't match a known field.
        name="dangerSecurityCode"
        autoComplete="off"
        placeholder="Type the security code to enable the action"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        error={Boolean(showError)}
        helperText={
          showError
            ? "Incorrect security code"
            : "Required for this destructive action"
        }
        inputProps={{
          "data-no-uppercase": "true",
          "data-form-type": "other",
          inputMode: "numeric",
          autoComplete: "off",
          // The "one-time-code" hint also helps Safari skip autofill.
          // No effect on Chrome but harmless.
          spellCheck: "false",
          // Mask the digits visually so casual onlookers can't read it,
          // without using type=password which triggers the autofill heuristic.
          style: { fontFamily: "monospace", letterSpacing: "0.1em" },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <LockIcon
                sx={{
                  fontSize: 18,
                  color: ok ? "success.main" : "warning.main",
                }}
              />
            </InputAdornment>
          ),
        }}
      />
      {!value && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 0.5, fontSize: "0.6875rem" }}
        >
          Ask your supervisor for the security code if you don't have it.
        </Typography>
      )}
    </Box>
  );
}
